import { useState } from 'react'
import { useAgreementDocuments, useUploadDocument, useDeleteDocument } from '@/hooks/api/useAgreements'
import { useDownloadDocument, useOpenDocument } from '@/hooks/api/useDocuments'
import { InvoiceData } from '@/domain/invoices/invoice'

export const useAgreementDocumentsManager = (agreementId: string) => {
  const [isUploading, setIsUploading] = useState(false)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)

  const { data: documents, isLoading: isLoadingDocuments, refetch: refetchDocuments } = useAgreementDocuments(agreementId)
  const { mutate: uploadDocument } = useUploadDocument()
  const { mutate: deleteDocument } = useDeleteDocument()
  const { mutate: downloadDocument } = useDownloadDocument()
  const { mutate: onOpenDocument } = useOpenDocument()
  
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0])
    }
  }

  const handleUpload = () => {
    if (!selectedFile || !agreementId) return

    setIsUploading(true)
    uploadDocument(
      { agreementId, file: selectedFile },
      {
        onSuccess: () => {
          refetchDocuments()
          setSelectedFile(null)
          const fileInput = document.getElementById('document-upload') as HTMLInputElement
          if (fileInput) fileInput.value = ''
        },
        onSettled: () => setIsUploading(false),
      }
    )
  }

  const handleDeleteDocument = (documentId: string) => {
    if (!agreementId) return

    deleteDocument(
      { agreementId, documentId },
      {
        onSuccess: () => {
          refetchDocuments()
        },
      }
    )
  }

  const handleDownloadDocument = (documentId: string, fileName: string) => {
    if (!agreementId) return

    downloadDocument({
      documentId,
      fileName,
    })
  }

  const onDownloadInvoice = (invoice: InvoiceData) => {
    if (!invoice.id || !invoice.fileName) return
    downloadDocument({
      documentId: invoice.id,
      fileName: invoice.fileName,
    })
  }

  return {
    documents,
    isLoadingDocuments,
    isUploading,
    selectedFile,
    handleFileChange,
    handleUpload,
    handleDeleteDocument,
    handleDownloadDocument,
    onDownloadInvoice,
    onOpenDocument
  }
}
